<script lang="ts">
    import Button from "@components/button/Button.svelte"
    import {
        LiveNotificationData,
        type NotificationDataType,
    } from "@utils/practice/notifications-data"
    import { createEventDispatcher } from "svelte"
    import NotificationEditor from "./NotificationEditor.svelte"
    import { practiceLessonMap, type PracticeAny } from "@circleofa/acim-engine"
    import { timeCrunchMachine, isGenerator } from "@utils/practice/reminder-time-conversion"
    import TimeSliderManager from "./slider/time-slider-manager.svelte"
    import ButtonGroup from "./slider/button-group.svelte"
    import { setLessonReminders } from "@utils/cap-notification-manager/cap-notification-process"
    import {
        Channel,
        clearAllNotifications,
        SUPPORTED_AT_TYPES,
    } from "@utils/cap-notification-manager/cap-notification-manager"
    import ReminderSkipPrompt from "./ReminderSkipPrompt.svelte"
    import type { ComponentProps } from "svelte"
    import { ReminderMemoryManagerSettings } from "@utils/practice/reminder-memory-manager"
    import type { PracticeLessonClass } from "@circleofa/acim-engine/dist/book/practices/practice"
    import { keys } from "lodash-es"
    import { slide } from "svelte/transition"

    export let notfData: NotificationDataType
    export let liveData: LiveNotificationData

    export let lesson: number

    export let notificationsSet: boolean

    /**
     * This needs to be the live version supplied, so live updating should
     * happen in the parent, and the propagated new version should be pushed
     * every time there's an update.
     */
    export let memoryManager: ReminderMemoryManagerSettings

    const dispatch = createEventDispatcher()
    const exit = () => dispatch("exit")

    /**
     * This may be undefined! Because of 0 (intro) and 366 (epilogue)
     */
    $: lessonPractice = practiceLessonMap.get(lesson)
    let loading = false

    function getLessonTaskBundle(practice: PracticeAny | undefined) {
        const time = practice?.time
        if (time === undefined) return undefined
        const generator = timeCrunchMachine(time)
        return {
            practice: practice as PracticeAny,
            generator,
            allowed:
                generator === null
                    ? true
                    : generator === "Temptation"
                      ? false
                      : SUPPORTED_AT_TYPES.has(generator.atType),
        }
    }

    const practiceMap: Record<PracticeAny["type"], Channel> = {
        "Frequent Reminders": Channel.FREQUENT_REMINDERS,
        "Hourly Remembrance": Channel.HOURLY_REMEMBRANCE,
        "Main Practice": Channel.MAIN_PRACTICE,
        "Morning and Evening Practice": Channel.MAIN_PRACTICE,
        Practice: Channel.MAIN_PRACTICE,
        "Response to Temptation": Channel.TEMPTATION,
    }

    function toTypeCategory(
        lessonPractices: PracticeLessonClass | undefined,
    ): Record<Channel, ReturnType<typeof getLessonTaskBundle>> {
        const obj = {} as Record<Channel, ReturnType<typeof getLessonTaskBundle>>
        if (!lessonPractices) return obj
        lessonPractices.practices.forEach((practice) => {
            const channel = practiceMap[practice.type]
            if (!channel) throw new Error("Channel not found!")
            obj[channel] = getLessonTaskBundle(practice)
        })
        return obj
    }

    $: lessonBundles = toTypeCategory(lessonPractice)

    /**
     * Different form enabled/disabled channels. These are to do with what the OS can handle, but the thing is
     * they're actually not to do with channels, they're to do with the type of notification (e.g. sometimes
     * the main channel can be either a freq reminder type or specific type). But when we set them, we need to
     * filter them by channel, so it gets confusing.
     * @param bundle
     */
    function filterAllowedChannels(
        bundle: Record<Channel, undefined | { allowed: boolean }>,
    ): Channel[] {
        return [
            ...(bundle[Channel.MAIN_PRACTICE]?.allowed ? [Channel.MAIN_PRACTICE] : []),
            ...(bundle[Channel.FREQUENT_REMINDERS]?.allowed ? [Channel.FREQUENT_REMINDERS] : []),
            ...(bundle[Channel.HOURLY_REMEMBRANCE]?.allowed ? [Channel.HOURLY_REMEMBRANCE] : []),
            ...(bundle[Channel.TEMPTATION]?.allowed ? [Channel.TEMPTATION] : []),
            ...(bundle[Channel.LESSON_REMINDER]?.allowed ? [Channel.LESSON_REMINDER] : []),
        ]
    }

    $: allowedChannels = filterAllowedChannels(lessonBundles)

    // Annoying thing cause there's no TS in html

    $: mainPracticeTask = lessonBundles[Channel.MAIN_PRACTICE]
    $: hourlyTask = lessonBundles[Channel.HOURLY_REMEMBRANCE]
    $: frequentTask = lessonBundles[Channel.FREQUENT_REMINDERS]
    $: temptationTask = lessonBundles[Channel.TEMPTATION]

    $: currentChannels = [...(keys(lessonBundles) as Channel[]), Channel.LESSON_REMINDER]

    $: console.log({ currentChannels })

    // When we need to prompt for
    let reminderPrompt: null | ComponentProps<ReminderSkipPrompt>["info"] = null
</script>

<!--

        disabled={$notificationPermissions !== NotificationPermissions.GRANTED ||
            $notificationStore?.pendingNotificationReminders?.lesson == lesson + 1}
 -->
<!-- TODO: Make sure you can't open this if we're in the 'looming' state of needing to go to the next lesson, otherwise
there will be some weird scheduling conflicts with "tomorrows" that apply to the day before -->
<div class="flex-col flex gap-4">
    <TimeSliderManager
        {notificationsSet}
        reactiveMemoryManager={$memoryManager}
        wakeup={notfData.wake}
        bufferedSleep={notfData.bufferedSleep}
        bufferedWake={notfData.bufferedWake}
        sleep={notfData.sleep}
        main={mainPracticeTask?.generator}
        mainAllowed={!!mainPracticeTask?.allowed}
        hourly={hourlyTask?.generator}
        hourlyAllowed={!!hourlyTask?.allowed}
        freq={frequentTask?.generator}
        freqAllowed={!!frequentTask?.allowed}
        temptation={!!temptationTask}
    />

    {#if !notificationsSet}
        <div class="flex-col flex gap-4" transition:slide>
            {#if mainPracticeTask && mainPracticeTask.generator !== null}
                <NotificationEditor
                    channel={Channel.MAIN_PRACTICE}
                    practice={mainPracticeTask.practice}
                    generator={mainPracticeTask.generator}
                    disabled={!mainPracticeTask.allowed}
                    icon="lotus"
                    reactiveMemoryManager={$memoryManager}
                    class="border-l-orange-400"
                />
            {/if}
            {#if hourlyTask && hourlyTask.generator !== null}
                <NotificationEditor
                    channel={Channel.HOURLY_REMEMBRANCE}
                    practice={hourlyTask.practice}
                    generator={hourlyTask.generator}
                    disabled={!hourlyTask.allowed}
                    icon="hourglass"
                    reactiveMemoryManager={$memoryManager}
                    class="border-l-red-400"
                />
            {/if}
            {#if frequentTask && frequentTask.generator !== null}
                <NotificationEditor
                    channel={Channel.FREQUENT_REMINDERS}
                    practice={frequentTask.practice}
                    generator={frequentTask.generator}
                    reactiveMemoryManager={$memoryManager}
                    disabled={!frequentTask.allowed}
                    icon="alarm"
                    class="border-l-purple-400"
                />
            {/if}

            {#if temptationTask && temptationTask.generator !== null}
                <NotificationEditor
                    channel={Channel.TEMPTATION}
                    practice={temptationTask.practice}
                    generator={temptationTask.generator}
                    reactiveMemoryManager={$memoryManager}
                    disabled={true}
                    icon="thunder"
                    class="border-l-green-500"
                />
            {/if}

            <!-- Notifications for tomorrow -->
            <NotificationEditor
                channel={Channel.LESSON_REMINDER}
                reactiveMemoryManager={$memoryManager}
                icon="sunset"
                class="border-l-blue-400"
            />
        </div>
    {/if}
</div>
<div class="m-4 flex items-center flex-col gap-2 -mb-0">
    {#if notificationsSet}
        <Button
            color="blue"
            go={() => {
                return clearAllNotifications()
            }}
            icon="refresh"
        >
            Reset reminders
        </Button>
    {:else}
        <Button
            color="blue"
            go={() => {
                return new Promise(async (res, rej) => {
                    const { number, pastRemindersExist, setAll, setFutureOnly } =
                        await setLessonReminders(
                            lesson,
                            notfData.wake,
                            notfData.sleep,
                            notfData.bufferedWake,
                            notfData.bufferedSleep,
                            memoryManager,
                            allowedChannels,
                        )
                    if (!pastRemindersExist) return res(setAll())
                    reminderPrompt = {
                        number,
                        onskip: () => {
                            res(setFutureOnly())
                            reminderPrompt = null
                        },
                        onnow: () => {
                            res(setAll())
                            reminderPrompt = null
                        },
                        cancel: () => {
                            rej()
                            reminderPrompt = null
                        },
                    }
                }).then(exit)
            }}
            icon="bell"
            disabled={!$memoryManager.atLeastOneChannelEnabled(currentChannels) ||
                loading ||
                notificationsSet}
        >
            Set reminders
        </Button>
    {/if}
    <Button class="p-0" color="white" naked on:click={exit}>
        {#if notificationsSet}
            Close
        {:else}
            Maybe later
        {/if}
    </Button>
</div>
{#if reminderPrompt}
    <ReminderSkipPrompt info={reminderPrompt} />
{/if}
<!-- {#if notification.pendingNextLessonReminders?.dateOfNext}
    <UnderDesc>
        Reminder currently set for {notification.pendingNextLessonReminders?.dateOfNext}
    </UnderDesc>
{/if} -->
